import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useEasykitDriverStore } from '@/stores/easykitDriver';
import { TYPE_PILL } from '@/utils/easykit';
import type { EasykitDriverTask } from '@/types/odoo';

const DUTCH_DAYS = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];

function getDateForOffset(offset: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d;
}

function formatDayLabel(offset: number): string {
  if (offset === 0) return 'Vandaag';
  if (offset === 1) return 'Morgen';
  const d = getDateForOffset(offset);
  const dayName = DUTCH_DAYS[d.getDay()];
  const day = d.getDate();
  const month = d.toLocaleDateString('nl-BE', { month: 'long' });
  return `${dayName} ${day} ${month}`;
}

function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const TYPE_COLORS: Record<string, { indicator: string; bg: string; border: string }> = {
  druktest: { indicator: 'bg-red-500', bg: 'bg-red-50', border: 'border-l-red-500' },
  levering: { indicator: 'bg-blue-500', bg: 'bg-blue-50', border: 'border-l-blue-500' },
  feestdag: { indicator: 'bg-purple-500', bg: 'bg-purple-50', border: 'border-l-purple-500' },
  other: { indicator: 'bg-gray-400', bg: 'bg-gray-50', border: 'border-l-gray-400' },
};

const TAG_CONFIG: Record<number, { label: string; cls: string }> = {
  7: { label: 'Voormiddag', cls: 'bg-green-100 text-green-800' },
  6: { label: 'Namiddag', cls: 'bg-blue-100 text-blue-800' },
  12: { label: 'Afhaling', cls: 'bg-amber-100 text-amber-800' },
};

function getPrimaryLabel(task: EasykitDriverTask): string {
  if (task.taskType === 'druktest' && task.soSummary) {
    const n = task.soSummary.collectors;
    return `${n} ${n === 1 ? 'collector' : 'collectoren'}`;
  }
  return task.soSummary?.headline ?? task.name;
}

function DriverTaskCard({ task }: { task: EasykitDriverTask }) {
  const color = TYPE_COLORS[task.taskType] || TYPE_COLORS.other;
  const pill = TYPE_PILL[task.taskType];
  const primaryLabel = getPrimaryLabel(task);

  return (
    <div className={`w-full text-left rounded-xl border border-gray-200 ${color.bg} border-l-4 ${color.border} p-4 min-h-[48px]`}>
      <div className="flex items-start gap-3">
        <div className={`w-2.5 h-2.5 rounded-full ${color.indicator} shrink-0 mt-1`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {pill && (
              <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${pill.cls}`}>
                {pill.label}
              </span>
            )}
            {task.prefix && (
              <span className="shrink-0 text-[11px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded truncate max-w-[60%]">
                {task.prefix}
              </span>
            )}
            <p className="text-sm font-bold text-td-navy truncate">{primaryLabel}</p>
          </div>
          <p className="text-sm text-td-navy/70 mt-0.5 truncate">{task.projectName}</p>
          {task.address && (
            <p className="text-xs text-td-muted mt-0.5 truncate">{task.address}</p>
          )}
          {task.tagIds && task.tagIds.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {task.tagIds.map(id => {
                const cfg = TAG_CONFIG[id];
                if (!cfg) return null;
                return <span key={id} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
              })}
            </div>
          )}
          {task.description && (
            <p className="text-xs text-td-muted mt-1.5 whitespace-pre-wrap line-clamp-2">{task.description}</p>
          )}
        </div>
        <svg className="w-5 h-5 text-gray-300 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
      <div className="h-4 bg-gray-100 rounded w-1/2 mb-3" />
      <div className="h-3 bg-gray-100 rounded w-2/3" />
    </div>
  );
}

function DriverDataTable({ tasks }: { tasks: EasykitDriverTask[] }) {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left px-5 py-3.5 text-xs font-bold text-gray-400 uppercase tracking-wider w-10">
              #
            </th>
            <th className="text-left px-5 py-3.5 text-xs font-bold text-gray-400 uppercase tracking-wider">
              Taak
            </th>
            <th className="text-left px-5 py-3.5 text-xs font-bold text-gray-400 uppercase tracking-wider">
              Project
            </th>
            <th className="text-left px-5 py-3.5 text-xs font-bold text-gray-400 uppercase tracking-wider">
              Adres
            </th>
            <th className="text-left px-5 py-3.5 text-xs font-bold text-gray-400 uppercase tracking-wider">
              Tags
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {tasks.map((task) => {
            const color = TYPE_COLORS[task.taskType] || TYPE_COLORS.other;
            const pill = TYPE_PILL[task.taskType];
            const primaryLabel = getPrimaryLabel(task);
            return (
              <tr key={task.id} className={`${color.bg} hover:brightness-95 cursor-pointer transition group`}>
                <td className="px-5 py-4">
                  <Link to={`/easykit/driver/task/${task.id}`} className="block">
                    <span className="text-sm text-gray-500 font-medium">{task.sequence}</span>
                  </Link>
                </td>
                <td className="px-5 py-4">
                  <Link to={`/easykit/driver/task/${task.id}`} className="block">
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${color.indicator} shrink-0`} />
                      {pill && (
                        <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${pill.cls}`}>
                          {pill.label}
                        </span>
                      )}
                      {task.prefix && (
                        <span className="shrink-0 text-[11px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded truncate max-w-[40%]">
                          {task.prefix}
                        </span>
                      )}
                      <span className="text-sm font-bold text-td-navy group-hover:text-td-blue transition">{primaryLabel}</span>
                    </div>
                    {task.description && (
                      <p className="text-xs text-td-muted mt-1 whitespace-pre-wrap line-clamp-2">{task.description}</p>
                    )}
                  </Link>
                </td>
                <td className="px-5 py-4">
                  <Link to={`/easykit/driver/task/${task.id}`} className="block">
                    <span className="text-sm text-td-navy/70">{task.projectName}</span>
                  </Link>
                </td>
                <td className="px-5 py-4">
                  <Link to={`/easykit/driver/task/${task.id}`} className="block">
                    <span className="text-sm text-td-muted">{task.address || '-'}</span>
                  </Link>
                </td>
                <td className="px-5 py-4">
                  <Link to={`/easykit/driver/task/${task.id}`} className="block">
                    <div className="flex flex-wrap gap-1">
                      {task.tagIds && task.tagIds.length > 0 ? (
                        task.tagIds.map((id) => {
                          const cfg = TAG_CONFIG[id];
                          if (!cfg) return null;
                          return (
                            <span key={id} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>
                              {cfg.label}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-sm text-gray-300">-</span>
                      )}
                    </div>
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function EasykitDriverView() {
  const { tasks, loading, error, dayOffset, fetchTasks, setDayOffset } = useEasykitDriverStore();

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const selectedDate = useMemo(() => getDateForOffset(dayOffset), [dayOffset]);
  const selectedDateKey = useMemo(() => formatDateKey(selectedDate), [selectedDate]);

  const filteredTasks = useMemo(() => {
    return tasks
      .filter((t) => t.dateDeadline.slice(0, 10) === selectedDateKey)
      .sort((a, b) => a.sequence - b.sequence);
  }, [tasks, selectedDateKey]);

  return (
    <div className="px-4 py-5 max-w-lg lg:max-w-5xl mx-auto">
      {/* Title + Refresh */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-extrabold text-td-navy">Mijn Taken</h1>
        <button
          type="button"
          onClick={() => fetchTasks()}
          disabled={loading}
          className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-50 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Vernieuwen"
        >
          <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Day selector */}
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => setDayOffset(dayOffset - 1)}
          disabled={dayOffset <= 0}
          className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-30 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Vorige dag"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="text-center">
          <p className="text-sm font-bold text-td-navy">{formatDayLabel(dayOffset)}</p>
          <p className="text-xs text-td-muted">{selectedDateKey}</p>
        </div>

        <button
          type="button"
          onClick={() => setDayOffset(dayOffset + 1)}
          disabled={dayOffset >= 5}
          className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-30 min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Volgende dag"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Loading */}
      {loading && tasks.length === 0 && (
        <div className="space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="font-semibold">Fout bij laden</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            type="button"
            onClick={() => fetchTasks()}
            className="mt-3 text-sm font-bold text-red-700 hover:text-red-800 underline min-h-[48px]"
          >
            Opnieuw proberen
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filteredTasks.length === 0 && (
        <div className="text-center py-16">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-td-muted text-sm font-medium">Geen taken voor deze dag</p>
        </div>
      )}

      {/* Desktop table */}
      {!error && filteredTasks.length > 0 && (
        <div className="hidden lg:block">
          <DriverDataTable tasks={filteredTasks} />
        </div>
      )}

      {/* Mobile cards */}
      {!error && filteredTasks.length > 0 && (
        <div className="lg:hidden space-y-2">
          {filteredTasks.map((task) => (
            <Link key={task.id} to={`/easykit/driver/task/${task.id}`} className="block">
              <DriverTaskCard task={task} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
